import { useState, useEffect, Fragment } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BackLink } from "@/components/BackLink";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, CalendarDays, ClipboardCheck, MapPin, CheckCircle2, FileSpreadsheet, Download, Receipt, UserPlus, Link2 as LinkIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HUB = `${API}/academy/attendance-hub`;

const dayLabel = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-SG", {
    weekday: "short", day: "numeric", month: "short",
  });

/** Everything attendance in one place: by year, by term, by day — plus the
 *  consolidated grid and the coach check-ins behind it. */
export default function AttendanceHub({ user }) {
  const isAdmin = ["convenor", "captain", "academy_admin"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  // The open tab lives in the URL so a refresh keeps you where you were, but it
  // REPLACES the entry: a tab is a view of one screen, not a screen of its own,
  // so Back leaves the page in one tap instead of walking the tabs.
  const tab = params.get("tab") || (isAdmin ? "review" : "sessions");
  const openTab = (value) => {
    const next = new URLSearchParams(window.location.search);
    if (next.get("tab") === value) return;
    next.set("tab", value);
    setParams(next, { replace: true });
  };

  const [calendar, setCalendar] = useState([]);
  const [year, setYear] = useState(null);
  const [termId, setTermId] = useState(null);
  const [teamFilter, setTeamFilter] = useState("all");
  const [teams, setTeams] = useState([]);
  // What this term actually has: real teams, or packages where sessions carry no team.
  const [groups, setGroups] = useState([]);
  const [showPast, setShowPast] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const [gridTeam, setGridTeam] = useState("");
  const [grid, setGrid] = useState(null);
  const [loadingGrid, setLoadingGrid] = useState(false);

  const [checkins, setCheckins] = useState([]);
  const [checkinFilter, setCheckinFilter] = useState("pending");
  const [pay, setPay] = useState(null);
  const [payFrom, setPayFrom] = useState(
    new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10));
  const [payTo, setPayTo] = useState(new Date().toISOString().slice(0, 10));
  const [duplicates, setDuplicates] = useState(null);
  const [dupFrom, setDupFrom] = useState("");
  const [dupTo, setDupTo] = useState("");
  const [combiningAll, setCombiningAll] = useState(false);
  const [combining, setCombining] = useState(null);
  const [unbilled, setUnbilled] = useState(null);
  const [charging, setCharging] = useState(null);
  const [reviewFrom, setReviewFrom] = useState(
    new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10));
  const [reviewTo, setReviewTo] = useState(new Date().toISOString().slice(0, 10));
  const [review, setReview] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);
  const [selected, setSelected] = useState([]);
  const [basis, setBasis] = useState("lowest");
  const [customRate, setCustomRate] = useState("");
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [invoicing, setInvoicing] = useState(false);
  const [linking, setLinking] = useState(null);
  const [openCoach, setOpenCoach] = useState(null);
  const [coachInvoices, setCoachInvoices] = useState(null);
  const [linkQuery, setLinkQuery] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkParents, setLinkParents] = useState([]);

  // A first trial is free and a covered session is already paid for: neither can
  // be ticked, counted or invoiced.
  const FREE = ["covered", "trial"];
  const billable = (review?.rows || []).filter(
    (r) => !FREE.includes(r.category) && !r.billed);

  const toggleRow = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const needsLink = Object.values(
    (review?.rows || []).filter((r) => r.needs_link).reduce((acc, r) => {
      const key = r.player_id || r.child_name;
      acc[key] = acc[key] || {
        player_id: r.player_id, name: r.child_name, contact: r.parent_contact,
        dates: [], attendance_ids: [],
      };
      acc[key].dates.push(r.date);
      acc[key].attendance_ids.push(r.attendance_id);
      return acc;
    }, {}));

  const searchChildren = async (q) => {
    if (!q || q.trim().length < 2) { setLinkResults([]); setLinkParents([]); return; }
    try {
      const r = await axios.get(`${API}/academy/attendance-hub/find-match`,
        { params: { q: q.trim() }, withCredentials: true });
      setLinkResults(r.data?.children || []);
      setLinkParents(r.data?.parents || []);
    } catch {
      setLinkResults([]);
      setLinkParents([]);
    }
  };

  // Parent on file, child never added: create the child, then link to it.
  const addChildUnderParent = async (parent) => {
    const typed = (linking?.name || "").replace(/^Unknown.*/i, "").trim();
    const childName = window.prompt(
      `Child's full name to add under ${parent.name || "this parent"}:`, typed);
    if (!childName || !childName.trim()) return;
    try {
      const r = await axios.post(`${API}/academy/attendance-hub/add-child-to-parent`,
        { parent_id: parent.parent_id, child_name: childName.trim() },
        { withCredentials: true });
      await linkWalkIn({ player_id: r.data.player_id, name: r.data.child_name });
      toast.success(r.data.already_existed
        ? `${r.data.child_name} was already on this parent — linked`
        : `${r.data.child_name} added under ${parent.name || "the parent"} and linked`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add the child");
    }
  };

  useEffect(() => {
    const t = setTimeout(() => searchChildren(linkQuery), 300);
    return () => clearTimeout(t);
  }, [linkQuery]);

  // Each tab fetches its own data the first time you land on it — including
  // when you arrive by Back, or straight from a bookmarked ?tab= URL.
  useEffect(() => {
    if (!isAdmin) return;
    if (tab === "checkins" && !checkins.length) loadCheckins(checkinFilter);
    if (tab === "review" && !review) loadReview();
    if (tab === "tofix" && !duplicates) { loadDuplicates(); loadUnbilled(); }
  }, [tab, isAdmin]);

  const linkWalkIn = async (child) => {
    const knownAs = linking.name && !linking.name.startsWith("Unknown") ? linking.name : "";
    try {
      const r = await axios.post(`${HUB}/link-attendance`, {
        from_player_id: linking.player_id,
        player_id: child.player_id || child.id,
        known_as: knownAs,
        attendance_ids: linking.attendance_ids,
      }, { withCredentials: true });
      toast.success(
        `${knownAs || "That attendance"} is now ${r.data.child_name}` +
        (r.data.alias_added ? ` · remembered as "${r.data.alias_added}"` : ""));
      setLinking(null);
      setLinkQuery("");
      setLinkResults([]);
      setLinkParents([]);
      loadReview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not link them");
    }
  };

  const previewInvoices = async () => {
    setInvoicing(true);
    try {
      const r = await axios.post(`${HUB}/bulk-invoice`,
        { attendance_ids: selected, basis, rate: Number(customRate) || undefined },
        { withCredentials: true });
      setInvoicePreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not work out those invoices");
    } finally {
      setInvoicing(false);
    }
  };

  const sendInvoices = async () => {
    setInvoicing(true);
    try {
      const r = await axios.post(`${HUB}/bulk-invoice`,
        { attendance_ids: selected, basis, rate: Number(customRate) || undefined, send: true },
        { withCredentials: true });
      toast.success(`${r.data.invoices} invoice(s) sent · $${r.data.total.toFixed(2)}`);
      if (r.data.failed?.length) {
        toast.error(`${r.data.failed.length} could not be sent — see the list`);
      }
      setInvoicePreview({ ...r.data, done: true });
      setSelected([]);
      loadReview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send those invoices");
    } finally {
      setInvoicing(false);
    }
  };

  const loadReview = async () => {
    setLoadingReview(true);
    try {
      const r = await axios.get(`${HUB}/attendance-review`, {
        params: { start_date: reviewFrom, end_date: reviewTo },
        withCredentials: true,
      });
      setReview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load that attendance");
    } finally {
      setLoadingReview(false);
    }
  };

  const downloadReview = async () => {
    try {
      const r = await axios.get(`${HUB}/attendance-review`, {
        params: { start_date: reviewFrom, end_date: reviewTo, format: "csv" },
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance-${reviewFrom}-to-${reviewTo}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error("Could not build the CSV");
    }
  };

  const loadPay = async () => {
    try {
      const r = await axios.get(`${HUB}/coach-pay`,
        { params: { start_date: payFrom, end_date: payTo }, withCredentials: true });
      setPay(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not work out coach pay");
    }
  };

  const downloadPay = async () => {
    try {
      const r = await axios.get(`${HUB}/coach-pay`, {
        params: { start_date: payFrom, end_date: payTo, format: "csv" },
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `coach-pay-${payFrom}-to-${payTo}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error("Could not build the CSV");
    }
  };

  const loadCoachInvoices = async () => {
    try {
      const r = await axios.get(`${API}/academy/coach-pay/invoices`, { withCredentials: true });
      setCoachInvoices(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load coach invoices");
    }
  };

  const setInvoiceStatus = async (inv, status) => {
    const reference = status === "paid"
      ? window.prompt(`Payment reference for ${inv.number} (optional):`, "") : "";
    if (reference === null) return;
    try {
      await axios.post(`${API}/academy/coach-pay/invoices/${inv.id}/status`,
        { status, payment_reference: reference }, { withCredentials: true });
      toast.success(`${inv.number} marked ${status}`);
      loadCoachInvoices();
      loadPay();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update that invoice");
    }
  };

  const markPaid = async (coach) => {
    if (!window.confirm(
      `Record ${coach.coach_name} as paid $${coach.due.toFixed(2)} for ${payFrom} to ${payTo}?`)) {
      return;
    }
    try {
      const r = await axios.post(`${API}/academy/coach-pay/mark-paid`, {
        coach_id: coach.coach_id, start_date: payFrom, end_date: payTo,
      }, { withCredentials: true });
      toast.success(`${coach.coach_name} settled · $${r.data.paid.toFixed(2)}`);
      loadPay();
      if (coachInvoices) loadCoachInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record that payment");
    }
  };


  const loadDuplicates = async () => {
    try {
      const r = await axios.get(`${HUB}/duplicates`, {
        params: {
          ...(dupFrom ? { from_date: dupFrom } : {}),
          ...(dupTo ? { to_date: dupTo } : {}),
        },
        withCredentials: true,
      });
      setDuplicates(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not scan for duplicates");
    }
  };

  const combineAll = async () => {
    setCombiningAll(true);
    try {
      const r = await axios.post(`${HUB}/combine-all`,
        { from_date: dupFrom, to_date: dupTo }, { withCredentials: true });
      toast.success(
        `${r.data.groups_combined} folded · ${r.data.sessions_archived} copies archived · ` +
        `${r.data.marks_moved} marks moved`);
      if (r.data.failed?.length) toast.error(`${r.data.failed.length} could not be folded`);
      loadDuplicates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fold those");
    } finally {
      setCombiningAll(false);
    }
  };

  const combine = async (group) => {
    setCombining(group.date + group.start_time);
    try {
      const r = await axios.post(`${HUB}/combine`, {
        session_ids: group.sessions.map((s) => s.id),
        keep_id: group.suggested_keep,
      }, { withCredentials: true });
      toast.success(`Combined — ${r.data.players} players, ${r.data.marks_moved} marks moved`);
      loadDuplicates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not combine those");
    } finally {
      setCombining(null);
    }
  };

  const loadUnbilled = async () => {
    try {
      const r = await axios.get(`${HUB}/unbilled-attendance`, { withCredentials: true });
      setUnbilled(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load unbilled sessions");
    }
  };

  const chargeChild = async (child) => {
    setCharging(child.player_id);
    try {
      const r = await axios.post(`${HUB}/unbilled-attendance/charge`, {
        player_id: child.player_id,
        attendance_ids: child.sessions.map((s) => s.attendance_id),
        rate: child.rate,
      }, { withCredentials: true });
      toast.success(`$${r.data.charged.toFixed(2)} added to the parent's account`);
      loadUnbilled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not charge that");
    } finally {
      setCharging(null);
    }
  };

  const setRange = (days, label) => {
    if (label === "lastweek") {
      const monday = new Date();
      monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7) - 7);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      setReviewFrom(monday.toISOString().slice(0, 10));
      setReviewTo(sunday.toISOString().slice(0, 10));
      return;
    }
    if (label === "thisweek") {
      const monday = new Date();
      monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
      setReviewFrom(monday.toISOString().slice(0, 10));
      setReviewTo(new Date().toISOString().slice(0, 10));
      return;
    }
    setReviewFrom(new Date(Date.now() - days * 864e5).toISOString().slice(0, 10));
    setReviewTo(new Date().toISOString().slice(0, 10));
  };

  useEffect(() => {
    (async () => {
      try {
        const [cal, tm] = await Promise.all([
          axios.get(`${HUB}/calendar`, { withCredentials: true }),
          axios.get(`${API}/academy/teams`, { withCredentials: true }),
        ]);
        const years = cal.data.years || [];
        setCalendar(years);
        setTeams(Array.isArray(tm.data) ? tm.data : tm.data.teams || []);
        if (years.length) {
          setYear(years[0].year);
          // Land on the term that's still being marked.
          const open = years[0].terms.find((t) => t.marked < t.sessions) || years[0].terms[0];
          setTermId(open?.term_id || null);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load the attendance calendar");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!termId) return;
    setLoadingSessions(true);
    axios.get(`${HUB}/sessions`, {
      params: { term_id: termId, ...(teamFilter !== "all" ? { team_id: teamFilter } : {}) },
      withCredentials: true,
    })
      .then((r) => {
        setSessions(r.data.sessions || []);
        if (teamFilter === "all") setGroups(r.data.groups || []);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load sessions"))
      .finally(() => setLoadingSessions(false));
  }, [termId, teamFilter]);

  const loadGrid = async (teamId) => {
    setGridTeam(teamId);
    if (!teamId || !termId) return;
    setLoadingGrid(true);
    try {
      const r = await axios.get(`${HUB}/grid`,
        { params: { term_id: termId, team_id: teamId }, withCredentials: true });
      setGrid(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the grid");
      setGrid(null);
    } finally {
      setLoadingGrid(false);
    }
  };

  const loadCheckins = async (status) => {
    setCheckinFilter(status);
    try {
      const r = await axios.get(`${HUB}/coach-checkins`,
        { params: { status }, withCredentials: true });
      setCheckins(r.data.checkins || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load check-ins");
    }
  };

  const decide = async (checkin, decision) => {
    try {
      await axios.post(`${API}/academy/admin/coach-checkins/${checkin.id}/decision`,
        { decision }, { withCredentials: true });
      toast.success(decision === "approved" ? "Check-in approved" : "Check-in rejected");
      loadCheckins(checkinFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that");
    }
  };

  const activeYear = calendar.find((y) => y.year === year);
  const byDate = sessions.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});
  // Oct 2026 — the list opened on the first day of the term, so a coach marking
  // today's register had to scroll past six weeks of history. Today first,
  // earlier days folded away but never hidden.
  const today = new Date().toISOString().slice(0, 10);
  const allDates = Object.keys(byDate).sort();
  const pastDates = allDates.filter((d) => d < today);
  const currentDates = allDates.filter((d) => d >= today);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-yellow-500/100" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] py-8 px-4" data-testid="attendance-hub">
      <div className="max-w-6xl mx-auto">
        <BackLink to={isAdmin ? "/academy" : "/academy/coach-dashboard"}
          className="text-slate-400 hover:text-white -ml-2 mb-2"
          testId="attendance-back" />
        <h1 className="text-2xl font-bold text-white mb-1">Attendance</h1>        <p className="text-sm text-slate-400 mb-6">
          Every session by year, term and day. Nothing is hidden — an old session can be
          marked whenever you get to it.
        </p>

        <Tabs value={tab} onValueChange={openTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="sessions" data-testid="tab-hub-sessions">
              <CalendarDays className="w-4 h-4 mr-1" /> Sessions
            </TabsTrigger>
            <TabsTrigger value="grid" data-testid="tab-hub-grid">
              <ClipboardCheck className="w-4 h-4 mr-1" /> Consolidated
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="checkins"
                data-testid="tab-hub-checkins">
                <MapPin className="w-4 h-4 mr-1" /> Coaches
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="review"
                data-testid="tab-hub-review">
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Who attended
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="tofix"
                data-testid="tab-hub-tofix">
                <ClipboardCheck className="w-4 h-4 mr-1" /> To fix
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="sessions">
            <div className="flex flex-wrap gap-2 mb-4" data-testid="year-tabs">
              {calendar.map((y) => (
                <Button key={y.year} size="sm"
                  variant={y.year === year ? "default" : "outline"}
                  onClick={() => {
                    setYear(y.year);
                    setTermId(y.terms[0]?.term_id || null);
                  }}
                  data-testid={`year-${y.year}`}>
                  {y.year}
                </Button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mb-4" data-testid="term-tabs">
              {(activeYear?.terms || []).map((t) => (
                <button key={t.term_id} onClick={() => setTermId(t.term_id)}
                  data-testid={`term-${t.term_id}`}
                  className={`text-left px-4 py-3 rounded-lg border transition-colors ${
                    t.term_id === termId
                      ? "border-yellow-500/100 bg-yellow-500/10"
                      : "border-white/10 bg-slate-900/50 hover:border-white/20"
                  }`}>
                  <span className="block text-sm font-semibold text-white">{t.term_name}</span>
                  <span className="block text-xs text-slate-400">
                    {t.days} training days · {t.marked} of {t.sessions} registers done
                  </span>
                </button>
              ))}
            </div>

            <div className="mb-4 max-w-xs">
              <Select value={teamFilter} onValueChange={setTeamFilter}>
                <SelectTrigger data-testid="team-filter">
                  <SelectValue placeholder="All teams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All my teams</SelectItem>
                  {(groups.length ? groups : teams).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loadingSessions ? (
              <div className="py-10 text-center">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
              </div>
            ) : Object.keys(byDate).length === 0 ? (
              <Card className="p-6 bg-slate-900/50 border-2 border-white/5 text-center text-slate-400"
                data-testid="hub-empty">
                No sessions in this term for that team.
              </Card>
            ) : (
              <div className="space-y-5">
                {currentDates.length === 0 && pastDates.length > 0 && !showPast && (
                  <p className="text-xs text-slate-500" data-testid="hub-no-upcoming">
                    Nothing left to come in this term.
                  </p>
                )}
                {(showPast ? allDates : currentDates).map((date) => (
                  <div key={date} data-testid={`day-${date}`}>
                    <h3 className="text-sm font-semibold text-slate-300 mb-2">
                      {dayLabel(date)}
                      {date === today && (
                        <span className="ml-2 text-[10px] bg-yellow-500/20 text-yellow-300 px-1.5 py-0.5 rounded">
                          today
                        </span>
                      )}
                    </h3>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {byDate[date].map((s) => (
                        <Link key={s.id} to={`/academy/attendance/${s.id}`}
                          data-testid={`hub-session-${s.id}`}>
                          <Card className="p-4 bg-slate-900/50 border-2 border-white/5 hover:border-yellow-500/40 transition-colors">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-semibold text-white truncate">{s.team_name}</p>
                                {(s.programmes || []).length > 0 && (
                                  <p className="text-[11px] text-yellow-300/80 truncate"
                                    title={(s.programmes || []).join(" · ")}
                                    data-testid={`hub-programme-${s.id}`}>
                                    {s.programmes.length > 1
                                      ? `${s.programmes.length} programmes · one register`
                                      : s.programmes[0]}
                                  </p>
                                )}
                                <p className="text-xs text-slate-400">
                                  {s.start_time || "--"} · {s.location || "No venue"}
                                </p>
                              </div>
                              {s.marked ? (
                                <Badge className={s.confirmed
                                  ? "bg-emerald-500/20 text-emerald-300"
                                  : "bg-yellow-500/20 text-yellow-300"}>
                                  {s.confirmed ? "Confirmed" : "Marked"}
                                </Badge>
                              ) : (
                                <Badge className="bg-slate-700 text-slate-300">Not marked</Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                              {s.signups} signed up · {s.present} present · {s.marked} marked
                              {s.copies > 1 && (
                                <span className="ml-1 text-slate-600"
                                  data-testid={`hub-folded-${s.id}`}>
                                  · {s.copies} programmes in one card
                                </span>
                              )}
                            </p>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
                {pastDates.length > 0 && (
                  <button
                    onClick={() => setShowPast((v) => !v)}
                    className="text-xs text-slate-400 hover:text-white underline"
                    data-testid="hub-toggle-past"
                  >
                    {showPast
                      ? "Hide earlier days"
                      : `Show ${pastDates.length} earlier day(s) in this term`}
                  </button>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="grid">
            <div className="mb-4 max-w-xs">
              <Select value={gridTeam} onValueChange={loadGrid}>
                <SelectTrigger data-testid="grid-team-select">
                  <SelectValue placeholder="Pick an age group" />
                </SelectTrigger>
                <SelectContent>
                  {(groups.length ? groups : teams).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loadingGrid ? (
              <div className="py-10 text-center">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
              </div>
            ) : !grid ? (
              <Card className="p-6 bg-slate-900/50 border-2 border-white/5 text-center text-slate-400">
                Pick an age group to see every player against every session this term.
              </Card>
            ) : (
              <Card className="p-4 bg-slate-900/50 border-2 border-white/5 overflow-x-auto"
                data-testid="attendance-grid">
                <p className="text-sm text-slate-300 mb-3">
                  <span className="font-semibold text-white">{grid.team.name}</span> ·{" "}
                  {grid.term.name} · {grid.totals.registers_taken} of {grid.totals.sessions}{" "}
                  registers taken
                </p>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-slate-400">
                      <th className="text-left px-2 py-2 sticky left-0 bg-slate-900">Player</th>
                      {grid.sessions.map((s) => (
                        <th key={s.id} className="px-2 py-2 whitespace-nowrap text-xs">
                          {dayLabel(s.date)}
                        </th>
                      ))}
                      <th className="px-2 py-2">Present</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grid.players.map((p) => (
                      <tr key={p.player_id} className="border-t border-white/5"
                        data-testid={`grid-row-${p.player_id}`}>
                        <td className="px-2 py-2 sticky left-0 bg-slate-900">
                          <span className="text-slate-200">{p.name}</span>
                          {p.days_per_week && (
                            <Badge className="ml-2 bg-slate-700 text-slate-300 text-[10px]">
                              {p.days_per_week}x/week
                            </Badge>
                          )}
                          {p.social && (
                            <Badge className="ml-1 bg-sky-500/20 text-sky-300 text-[10px]">
                              Social
                            </Badge>
                          )}
                        </td>
                        {grid.sessions.map((s) => {
                          const mark = p.marks[s.id];
                          return (
                            <td key={s.id} className="px-2 py-2 text-center">
                              {mark === "present" ? (
                                <span className="text-emerald-400">P</span>
                              ) : mark === "absent" ? (
                                <span className="text-red-400">A</span>
                              ) : mark === "mc" ? (
                                <span className="text-yellow-300">MC</span>
                              ) : (
                                <span className="text-slate-600">·</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 text-center font-semibold text-white">
                          {p.present}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </TabsContent>

          {isAdmin && (
            <TabsContent value="checkins">
              <div className="flex flex-wrap gap-2 mb-4">
                {["pending", "approved", "all"].map((f) => (
                  <Button key={f} size="sm" variant={checkinFilter === f ? "default" : "outline"}
                    onClick={() => loadCheckins(f)} data-testid={`checkin-filter-${f}`}>
                    {f === "pending" ? "Needs approval" : f === "approved" ? "Approved" : "All"}
                  </Button>
                ))}
                <Button size="sm" variant="outline" onClick={loadPay}
                  data-testid="load-pay-btn">
                  Work out pay
                </Button>
              </div>

              {pay && (
                <Card className="p-4 mb-4 bg-slate-900/50 border-2 border-white/5"
                  data-testid="coach-pay-card">
                  <div className="flex flex-wrap items-end gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">From</label>
                      <input type="date" value={payFrom}
                        onChange={(e) => setPayFrom(e.target.value)}
                        className="bg-slate-950/60 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200"
                        data-testid="pay-from" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">To</label>
                      <input type="date" value={payTo}
                        onChange={(e) => setPayTo(e.target.value)}
                        className="bg-slate-950/60 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200"
                        data-testid="pay-to" />
                    </div>
                    <Button size="sm" onClick={loadPay} data-testid="pay-refresh">Refresh</Button>
                    <Button size="sm" variant="outline" onClick={downloadPay}
                      disabled={!pay?.coaches?.length} data-testid="pay-csv">
                      <Download className="w-4 h-4 mr-1" /> Download CSV
                    </Button>
                    <Button size="sm" variant="outline" onClick={loadCoachInvoices}
                      data-testid="load-coach-invoices">
                      <Receipt className="w-4 h-4 mr-1" /> Coach invoices
                    </Button>
                  </div>
                  {coachInvoices && (
                    <Card className="p-4 bg-slate-900/70 border border-white/10 mb-4"
                      data-testid="coach-invoice-list">
                      <p className="text-sm text-white font-semibold mb-1">
                        Coach invoices · ${coachInvoices.owed.toFixed(2)} outstanding
                        <span className="text-xs text-slate-400 font-normal ml-2">
                          ${coachInvoices.paid.toFixed(2)} paid
                        </span>
                      </p>
                      {!coachInvoices.invoices.length ? (
                        <p className="text-xs text-slate-400">None raised yet.</p>
                      ) : coachInvoices.invoices.map((inv) => (
                        <div key={inv.id}
                          className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-white/5 last:border-0"
                          data-testid={`coach-invoice-${inv.id}`}>
                          <span className="text-sm text-slate-200">
                            {inv.number} · {inv.coach_name}
                            <span className="text-xs text-slate-500 ml-2">
                              {inv.period_start} to {inv.period_end} · {inv.session_count} sessions
                            </span>
                          </span>
                          <span className="flex items-center gap-2">
                            <Badge className={inv.status === "paid"
                              ? "bg-emerald-500/15 text-emerald-300"
                              : "bg-amber-500/15 text-amber-300"}>{inv.status}</Badge>
                            <span className="text-sm font-semibold text-white">
                              ${inv.total.toFixed(2)}
                            </span>
                            <a href={`${API}/academy/coach-pay/invoices/${inv.id}/document`}
                              target="_blank" rel="noreferrer"
                              className="text-xs text-slate-300 underline"
                              data-testid={`coach-invoice-view-${inv.id}`}>view</a>
                            {inv.status !== "paid" && (
                              <Button size="sm" variant="outline"
                                onClick={() => setInvoiceStatus(inv, "paid")}
                                data-testid={`mark-paid-${inv.id}`}>Mark paid</Button>
                            )}
                          </span>
                        </div>
                      ))}
                    </Card>
                  )}
                  {pay.coaches.length === 0 ? (
                    <p className="text-sm text-slate-400" data-testid="pay-empty">
                      No check-ins in those dates.
                    </p>
                  ) : (
                    <table className="min-w-full text-sm" data-testid="pay-table">
                      <thead>
                        <tr className="text-slate-400 text-xs">
                          <th className="text-left px-2 py-1">Coach</th>
                          <th className="px-2 py-1">Sessions</th>
                          <th className="px-2 py-1">Hours</th>
                          <th className="px-2 py-1">Awaiting approval</th>
                          <th className="px-2 py-1">Rate</th>
                          <th className="px-2 py-1">Still owed</th>
                          <th className="px-2 py-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pay.coaches.map((c) => (
                          <Fragment key={c.coach_id}>
                          <tr className="border-t border-white/5 cursor-pointer hover:bg-white/5"
                            onClick={() => setOpenCoach(openCoach === c.coach_id ? null : c.coach_id)}
                            data-testid={`pay-row-${c.coach_id}`}>
                            <td className="px-2 py-2 text-slate-200">
                              {c.coach_name}
                              <span className="text-xs text-slate-500 ml-2">
                                {openCoach === c.coach_id ? "hide sessions" : "show sessions"}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-center">{c.approved_sessions}</td>
                            <td className="px-2 py-2 text-center">{c.hours || "-"}</td>
                            <td className="px-2 py-2 text-center text-amber-300">
                              {c.pending_sessions || "-"}
                            </td>
                            <td className="px-2 py-2 text-center text-slate-400">
                              {c.rate
                                ? `$${c.rate} ${c.basis === "per_hour" ? "/hr" : "/session"}`
                                : "not set"}
                              {!!(c.extra_rates || []).length && (
                                <span className="text-xs text-slate-500">
                                  {" "}+{c.extra_rates.length}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-center font-semibold text-white">
                              {c.missing_rate ? "no rate set" : `$${c.due.toFixed(2)}`}
                              {c.invoiced_amount > 0 && (
                                <span className="block text-[10px] text-slate-500 font-normal">
                                  ${c.invoiced_amount.toFixed(2)} invoiced
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-center">
                              {c.due > 0 && (
                                <Button size="sm" variant="outline"
                                  onClick={(e) => { e.stopPropagation(); markPaid(c); }}
                                  data-testid={`pay-mark-${c.coach_id}`}>
                                  Mark paid
                                </Button>
                              )}
                            </td>
                          </tr>
                          {openCoach === c.coach_id && (
                            <tr key={`${c.coach_id}-detail`} className="bg-slate-950/40">
                              <td colSpan={7} className="px-4 py-3">
                                <table className="w-full text-xs" data-testid={`pay-sessions-${c.coach_id}`}>
                                  <thead>
                                    <tr className="text-slate-500 text-left">
                                      <th className="py-1">Date</th>
                                      <th className="py-1">Team</th>
                                      <th className="py-1">Programme</th>
                                      <th className="py-1">Hours</th>
                                      <th className="py-1">Rate used</th>
                                      <th className="py-1 text-right">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {c.sessions.map((s) => (
                                      <tr key={s.session_id} className="border-t border-white/5"
                                        data-testid={`pay-session-${s.session_id}`}>
                                        <td className="py-1.5 text-slate-300 whitespace-nowrap">
                                          {s.date ? dayLabel(s.date) : "-"}
                                          {s.start_time ? ` ${s.start_time}` : ""}
                                        </td>
                                        <td className="py-1.5 text-slate-400">
                                          {s.team}
                                          {!!(s.also_covered || []).length && (
                                            <span className="text-slate-500">
                                              {" "}+ {s.also_covered.join(", ")}
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-1.5 text-slate-400">{s.programme}</td>
                                        <td className="py-1.5 text-slate-400">{s.hours || "-"}</td>
                                        <td className="py-1.5 text-slate-400">
                                          {s.rate
                                            ? `$${s.rate} ${s.basis === "per_hour" ? "/hr" : "/session"}`
                                            : "none"}
                                          {s.rate_label && s.rate_label !== "default" && (
                                            <span className="text-slate-500"> · {s.rate_label}</span>
                                          )}
                                        </td>
                                        <td className="py-1.5 text-right">
                                          {!s.approved ? (
                                            <span className="text-amber-300">awaiting approval</span>
                                          ) : s.covered_by ? (
                                            <span className="text-slate-500">{s.covered_by}</span>
                                          ) : (
                                            <span className="text-slate-200">${s.amount.toFixed(2)}</span>
                                          )}
                                          {!s.gps_verified && s.approved && (
                                            <span className="text-slate-500"> · no GPS</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <p className="text-xs text-slate-500 mt-3">
                    Click a coach to see every session behind the figure. Only approved check-ins are
                    paid; set rates under Coach Management, where a coach can hold more than one.
                  </p>
                </Card>
              )}
              {checkins.length === 0 ? (
                <Card className="p-6 bg-slate-900/50 border-2 border-white/5 text-center text-slate-400"
                  data-testid="checkins-empty">
                  Nothing here. Coaches check in from the register screen when they arrive.
                </Card>
              ) : (
                <div className="space-y-2" data-testid="checkin-list">
                  {checkins.map((c) => (
                    <Card key={c.id} className="p-4 bg-slate-900/50 border-2 border-white/5"
                      data-testid={`checkin-${c.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-white">
                            {c.coach_name}
                            <span className="text-slate-400 font-normal"> · {c.team_name}</span>
                          </p>
                          <p className="text-xs text-slate-400">
                            {new Date(c.checkin_time).toLocaleString("en-SG")} ·{" "}
                            {c.location_name || "No venue"}
                            {c.distance_from_venue_meters != null
                              && ` · ${c.distance_from_venue_meters}m away`}
                          </p>
                          <p className="text-xs text-slate-500">
                            {c.verification_status === "no_gps"
                              ? "No GPS — checked in manually"
                              : c.verification_status === "outside_radius"
                              ? "Outside the venue radius"
                              : c.verification_status === "verified"
                              ? "Verified at the venue"
                              : "Venue has no coordinates on file"}
                            {c.session_cancelled && " · session was cancelled"}
                            {c.register_taken ? " · register taken" : " · register not taken"}
                            {c.work_type === "combined" && " · coached both teams together, paid once"}
                            {c.work_type === "register_only" && " · marked the register only, did not coach, not paid"}
                            {c.reason ? ` · "${c.reason}"` : ""}
                          </p>
                        </div>
                        {c.approval_status === "pending" ? (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => decide(c, "approved")}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`approve-${c.id}`}>
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => decide(c, "rejected")}
                              data-testid={`reject-${c.id}`}>
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <Badge className={c.approval_status === "approved"
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "bg-red-500/20 text-red-300"}>
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {c.approval_status}
                          </Badge>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="review">
              <Card className="p-4 bg-slate-900/50 border-2 border-white/5 mb-4"
                data-testid="review-controls">
                <h3 className="font-semibold text-white mb-1">
                  Who attended, and what it would cost
                </h3>
                <p className="text-xs text-slate-400 mb-4">
                  Every present mark in the range, priced three ways. Decide what a week
                  deserves before charging anyone.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">From</label>
                    <input type="date" value={reviewFrom}
                      onChange={(e) => setReviewFrom(e.target.value)}
                      className="bg-slate-800 border border-white/10 rounded px-3 py-1.5 text-sm text-white"
                      data-testid="review-from" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">To</label>
                    <input type="date" value={reviewTo}
                      onChange={(e) => setReviewTo(e.target.value)}
                      className="bg-slate-800 border border-white/10 rounded px-3 py-1.5 text-sm text-white"
                      data-testid="review-to" />
                  </div>
                  <Button size="sm" onClick={loadReview} disabled={loadingReview}
                    data-testid="review-load">
                    {loadingReview ? "Reading the registers..." : "Show me"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadReview}
                    disabled={!review || !review.rows.length} data-testid="review-csv">
                    <Download className="w-4 h-4 mr-1" /> Download CSV
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {[["Last week", "lastweek"], ["This week", "thisweek"],
                    ["Last 30 days", "30"]].map(([label, key]) => (
                    <button key={key}
                      onClick={() => { setRange(Number(key) || 7, key); }}
                      className="text-xs px-3 py-1 rounded-full border border-white/10 text-slate-300 hover:border-emerald-500/50 hover:text-emerald-300 transition-colors"
                      data-testid={`review-range-${key}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </Card>

              {review && (
                <>
                  {!!(review.days || []).length && (
                    <Card className="bg-slate-900/50 border-2 border-white/5 mb-4 overflow-x-auto"
                      data-testid="review-days">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-slate-400 border-b border-white/10">
                            <th className="p-3">Day</th>
                            <th className="p-3">Teams out</th>
                            <th className="p-3">Sessions</th>
                            <th className="p-3">Registers taken</th>
                            <th className="p-3">Present</th>
                          </tr>
                        </thead>
                        <tbody>
                          {review.days.map((d) => (
                            <tr key={d.date} className="border-b border-white/5 last:border-0"
                              data-testid={`review-day-${d.date}`}>
                              <td className="p-3 text-slate-200 whitespace-nowrap">
                                {dayLabel(d.date)}
                              </td>
                              <td className="p-3 text-slate-400">{d.teams.length}</td>
                              <td className="p-3 text-slate-300">
                                {d.sessions}
                                {d.cancelled > 0 && (
                                  <span className="text-slate-500"> · {d.cancelled} cancelled</span>
                                )}
                              </td>
                              <td className="p-3">
                                <span className="text-slate-300">{d.registers_taken}</span>
                                {d.registers_missing_count > 0 && (
                                  <span className="ml-2 text-amber-300 text-xs"
                                    data-testid={`review-missing-${d.date}`}>
                                    {d.registers_missing_count} not taken
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-slate-300">{d.present}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  )}

                  <div className="grid gap-3 sm:grid-cols-3 mb-4" data-testid="review-totals">
                    {[
                      ["Lowest package rate", review.totals.lowest, "the cheapest a registered child pays"],
                      ["Their package rate", review.totals.package, "what a member pays"],
                      ["Walk-in rate", review.totals.premium, "the full one-off rate"],
                    ].map(([label, value, hint]) => (
                      <Card key={label} className="p-4 bg-slate-900/50 border-2 border-white/5">
                        <p className="text-xs text-slate-400">{label}</p>
                        <p className="text-2xl font-bold text-white">${value.toFixed(2)}</p>
                        <p className="text-xs text-slate-500">{hint}</p>
                      </Card>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mb-3">
                    {review.totals.sessions} present marks · {review.totals.children} children
                    {Object.entries(review.by_category).map(([key, c]) => (
                      <span key={key} className="ml-2" data-testid={`review-cat-${key}`}>
                        · {c.label}: {c.sessions}
                      </span>
                    ))}
                  </p>

                  {!!needsLink.length && (
                    <Card className="p-4 bg-purple-500/5 border-2 border-purple-500/20 mb-4"
                      data-testid="needs-link-card">
                      <p className="text-sm text-white font-semibold">
                        {needsLink.length} walk-in{needsLink.length === 1 ? "" : "s"} with nobody to bill
                      </p>
                      <p className="text-xs text-slate-400 mb-3">
                        Point each one at the child they really are — every date in this range follows
                        them, and the name is remembered for next time.
                      </p>
                      {needsLink.map((g) => (
                        <div key={g.player_id || g.name}
                          className="py-2 border-b border-white/5 last:border-0"
                          data-testid={`needs-link-${g.player_id}`}>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm text-slate-200">
                              {g.name}
                              <span className="text-xs text-slate-500 ml-2">
                                {g.dates.length} session{g.dates.length === 1 ? "" : "s"} ·{" "}
                                {g.dates.map(dayLabel).join(", ")}
                                {g.contact ? ` · ${g.contact}` : " · no contact"}
                              </span>
                            </span>
                            <Button size="sm" variant="outline"
                              onClick={() => {
                                setLinking(linking?.player_id === g.player_id ? null : g);
                                // "Unknown — no player record" is not a name to search for.
                                setLinkQuery(g.name && !g.name.startsWith("Unknown") ? g.name : "");
                              }}
                              data-testid={`link-btn-${g.player_id}`}>
                              <LinkIcon className="w-3 h-3 mr-1" />
                              {linking?.player_id === g.player_id ? "Cancel" : "Link to a child"}
                            </Button>
                          </div>

                          {linking?.player_id === g.player_id && (
                            <div className="mt-3 pl-1">
                              <input value={linkQuery}
                                onChange={(e) => setLinkQuery(e.target.value)}
                                placeholder="Child, parent, mobile or email"
                                className="w-full max-w-md bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm text-white"
                                data-testid="link-search" />
                              <p className="text-[11px] text-slate-500 mt-1">
                                Searches the child&apos;s name and nickname, the parent&apos;s name,
                                their mobile and their email.
                              </p>
                              <div className="mt-2 space-y-1 max-h-72 overflow-y-auto">
                                {linkResults.map((c) => (
                                  <button key={c.player_id} onClick={() => linkWalkIn(c)}
                                    className="w-full text-left px-3 py-2 rounded bg-slate-800/60 hover:bg-slate-700 text-sm text-slate-200 transition-colors"
                                    data-testid={`link-choice-${c.player_id}`}>
                                    <span className="text-slate-100">{c.name}</span>
                                    <span className="text-xs text-slate-500 ml-2">
                                      {[c.age_group, c.team_name].filter(Boolean).join(" · ")}
                                      {c.matched_on === "parent" && " · matched on the parent"}
                                      {c.matched_on === "nickname" && " · known by that nickname"}
                                    </span>
                                    {(c.parent?.name || c.parent?.phone || c.parent?.email) && (
                                      <span className="block text-[11px] text-slate-500">
                                        {[c.parent?.name, c.parent?.phone, c.parent?.email]
                                          .filter(Boolean).join(" · ")}
                                      </span>
                                    )}
                                  </button>
                                ))}

                                {linkParents.map((p) => (
                                  <div key={p.parent_id || p.email || p.phone}
                                    className="px-3 py-2 rounded bg-sky-500/5 border border-sky-500/20"
                                    data-testid={`link-parent-${p.parent_id || p.email}`}>
                                    <p className="text-sm text-slate-200">
                                      {p.name || "Parent"}
                                      <span className="text-[11px] text-slate-500 ml-2">
                                        {[p.phone, p.email].filter(Boolean).join(" · ")}
                                      </span>
                                    </p>
                                    {p.children?.length > 0 ? (
                                      <div className="mt-1 space-y-1">
                                        <p className="text-[11px] text-slate-400">
                                          Their children on file — pick the right one:
                                        </p>
                                        {p.children.map((k) => (
                                          <button key={k.player_id}
                                            onClick={() => linkWalkIn(k)}
                                            className="block w-full text-left px-2 py-1 rounded bg-slate-800/60 hover:bg-slate-700 text-xs text-slate-200"
                                            data-testid={`link-parent-child-${k.player_id}`}>
                                            {k.name}
                                            {k.age_group && (
                                              <span className="text-slate-500 ml-2">{k.age_group}</span>
                                            )}
                                          </button>
                                        ))}
                                      </div>
                                    ) : p.child_count > 0 ? (
                                      <p className="text-[11px] text-slate-500 mt-1">
                                        Their {p.child_count === 1 ? "child is" : "children are"} listed
                                        above — or add another below.
                                      </p>
                                    ) : (
                                      <p className="text-[11px] text-amber-300 mt-1">
                                        This parent has no child on file.
                                      </p>
                                    )}
                                    {p.has_account ? (
                                      <Button size="sm" variant="outline" className="mt-2 h-7 text-xs"
                                        onClick={() => addChildUnderParent(p)}
                                        data-testid={`add-child-${p.parent_id}`}>
                                        <UserPlus className="w-3 h-3 mr-1" />
                                        Add the child under this parent &amp; link
                                      </Button>
                                    ) : (
                                      <p className="text-[11px] text-slate-500 mt-1">
                                        Known from a registration only — no portal account yet.
                                      </p>
                                    )}
                                  </div>
                                ))}

                                {linkQuery.length >= 2 && !linkResults.length && !linkParents.length && (
                                  <p className="text-xs text-slate-500 px-1">
                                    Nothing matched that child, parent, mobile or email.
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </Card>
                  )}

                  {!!billable.length && (
                    <Card className="p-4 bg-amber-500/5 border-2 border-amber-500/20 mb-4"
                      data-testid="bulk-invoice-bar">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-[220px]">
                          <p className="text-sm text-white font-semibold">
                            Invoice the sessions nobody has paid for
                          </p>
                          <p className="text-xs text-slate-400">
                            {selected.length} of {billable.length} ticked · one invoice per parent,
                            listing every date
                          </p>
                        </div>
                        <button
                          onClick={() => setSelected(billable.map((r) => r.attendance_id))}
                          className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-300 hover:border-amber-500/50 hover:text-amber-300 transition-colors"
                          data-testid="select-all-billable">
                          Tick all {billable.length}
                        </button>
                        {!!selected.length && (
                          <button onClick={() => { setSelected([]); setInvoicePreview(null); }}
                            className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-400 hover:text-white transition-colors"
                            data-testid="clear-selection">
                            Clear
                          </button>
                        )}
                        <select value={basis} onChange={(e) => { setBasis(e.target.value); setInvoicePreview(null); }}
                          className="bg-slate-800 border border-white/10 rounded px-3 py-1.5 text-sm text-white"
                          data-testid="invoice-basis">
                          <option value="lowest">Lowest package rate</option>
                          <option value="package">Their package rate</option>
                          <option value="premium">Walk-in rate</option>
                          <option value="custom">My own rate</option>
                        </select>
                        {basis === "custom" && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 text-sm">$</span>
                            <input type="number" min="1" step="0.01" value={customRate}
                              onChange={(e) => { setCustomRate(e.target.value); setInvoicePreview(null); }}
                              placeholder="34.00"
                              className="w-24 bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                              data-testid="invoice-custom-rate" />
                            <span className="text-slate-500 text-xs">per session</span>
                          </div>
                        )}
                        <Button size="sm" onClick={previewInvoices}
                          disabled={!selected.length || invoicing
                            || (basis === "custom" && !(Number(customRate) > 0))}
                          data-testid="preview-invoices">
                          <Receipt className="w-4 h-4 mr-1" />
                          {invoicing ? "Working..." : "Preview invoices"}
                        </Button>
                      </div>

                      {invoicePreview && (
                        <div className="mt-4 border-t border-white/10 pt-4" data-testid="invoice-preview">
                          {(invoicePreview.parents || invoicePreview.sent || []).map((p, i) => (
                            <div key={i}
                              className="flex items-start justify-between py-1.5 text-sm border-b border-white/5 last:border-0">
                              <span className="text-slate-200">
                                {p.parent_name || p.parent_email}
                                {!!(p.children || []).length && (
                                  <span className="block text-xs text-slate-400">
                                    {p.children.map((c) =>
                                      `${c.name}: ${c.dates.map(dayLabel).join(", ")} at $${c.rate.toFixed(2)}`
                                    ).join(" · ")}
                                  </span>
                                )}
                                <span className="text-slate-500 text-xs">
                                  {p.sessions} session{p.sessions === 1 ? "" : "s"}
                                  {p.invoice_reference ? ` · ${p.invoice_reference}` : ""}
                                </span>
                              </span>
                              <span className="text-slate-100 font-semibold whitespace-nowrap">
                                ${p.amount.toFixed(2)}
                              </span>
                            </div>
                          ))}
                          {invoicePreview.warning && (
                            <p className="mt-3 text-xs text-amber-300" data-testid="invoice-warning">
                              {invoicePreview.warning}
                            </p>
                          )}
                          {!!(invoicePreview.blocked || []).length && (
                            <div className="mt-3 text-xs text-amber-300" data-testid="invoice-blocked">
                              {invoicePreview.blocked.length} row(s) can't be invoiced:
                              {" "}{invoicePreview.blocked.map((b) => `${b.child_name} (${b.reason})`).join(", ")}
                            </div>
                          )}
                          {!!(invoicePreview.failed || []).length && (
                            <div className="mt-3 text-xs text-red-300" data-testid="invoice-failed">
                              {invoicePreview.failed.map((f) => `${f.parent_name}: ${f.error}`).join("; ")}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-4">
                            <p className="text-sm text-slate-300">
                              {invoicePreview.done
                                ? `${invoicePreview.invoices} invoice(s) sent`
                                : `${invoicePreview.invoices} invoice(s) · $${invoicePreview.total.toFixed(2)} total`}
                            </p>
                            {!invoicePreview.done && !!invoicePreview.invoices && (
                              <Button size="sm" onClick={sendInvoices} disabled={invoicing}
                                className="bg-amber-500 hover:bg-amber-600 text-slate-950"
                                data-testid="send-invoices">
                                {invoicing ? "Sending..." : `Send ${invoicePreview.invoices} invoice(s)`}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </Card>
                  )}

                  {review.rows.length === 0 ? (
                    <Card className="p-6 bg-slate-900/50 border-2 border-white/5 text-center text-slate-400"
                      data-testid="review-empty">
                      Nobody was marked present in those dates.
                    </Card>
                  ) : (
                    <Card className="bg-slate-900/50 border-2 border-white/5 overflow-x-auto">
                      <table className="w-full text-sm" data-testid="review-table">
                        <thead>
                          <tr className="text-left text-xs text-slate-400 border-b border-white/10">
                            <th className="p-3 w-8"></th>
                            <th className="p-3">Date</th>
                            <th className="p-3">Child</th>
                            <th className="p-3">Team</th>
                            <th className="p-3">Status</th>
                            <th className="p-3 text-right">Lowest</th>
                            <th className="p-3 text-right">Package</th>
                            <th className="p-3 text-right">Walk-in</th>
                          </tr>
                        </thead>
                        <tbody>
                          {review.rows.map((r) => (
                            <tr key={r.attendance_id}
                              className="border-b border-white/5 last:border-0"
                              data-testid={`review-row-${r.attendance_id}`}>
                              <td className="p-3">
                                {!FREE.includes(r.category) && !r.billed && (
                                  <input type="checkbox"
                                    checked={selected.includes(r.attendance_id)}
                                    onChange={() => toggleRow(r.attendance_id)}
                                    className="accent-amber-500"
                                    data-testid={`pick-${r.attendance_id}`} />
                                )}
                              </td>
                              <td className="p-3 text-slate-300 whitespace-nowrap">
                                {dayLabel(r.date)}
                              </td>
                              <td className="p-3 text-slate-200">
                                {r.child_name}
                                {r.days_per_week && (
                                  <span className="text-slate-500"> · {r.days_per_week}/wk</span>
                                )}
                                {r.is_walk_in && r.parent_contact && (
                                  <span className="block text-xs text-slate-500">
                                    walk-in · {r.parent_contact}
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-slate-400">{r.team}</td>
                              <td className="p-3">
                                <Badge className={
                                  r.category === "covered"
                                    ? "bg-emerald-500/15 text-emerald-300"
                                    : r.category === "trial"
                                      ? "bg-sky-500/15 text-sky-300"
                                      : r.category === "beyond_package" || r.category === "extra_trial"
                                        ? "bg-amber-500/15 text-amber-300"
                                        : "bg-red-500/15 text-red-300"}>
                                  {r.category_label}
                                </Badge>
                                {r.billed && (
                                  <span className="ml-2 text-xs text-slate-400">
                                    billed ${r.billed_amount?.toFixed(2)}
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-right text-slate-300">
                                ${r.lowest_rate.toFixed(2)}
                              </td>
                              <td className="p-3 text-right text-slate-300">
                                {r.package_rate ? `$${r.package_rate.toFixed(2)}` : "-"}
                              </td>
                              <td className="p-3 text-right text-slate-300">
                                ${r.premium_rate.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  )}
                </>
              )}
            </TabsContent>
          )}


          {isAdmin && (
            <TabsContent value="tofix">
              <div className="space-y-8">
                <Card className="p-4 bg-slate-900/50 border-2 border-white/5"
                  data-testid="duplicates-card">
                  <h3 className="font-semibold text-white mb-1">
                    The same session, saved more than once
                  </h3>
                  <p className="text-xs text-slate-400 mb-4">
                    Each programme generates its own copy of a shared slot. Combining keeps every
                    sign-up and every mark on one register and archives the rest — nothing is deleted.
                  </p>
                  <div className="flex flex-wrap items-end gap-3 mb-4">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">From</label>
                      <input type="date" value={dupFrom}
                        onChange={(e) => setDupFrom(e.target.value)}
                        className="bg-slate-800 border border-white/10 rounded px-3 py-1.5 text-sm text-white"
                        data-testid="dup-from" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">To</label>
                      <input type="date" value={dupTo}
                        onChange={(e) => setDupTo(e.target.value)}
                        className="bg-slate-800 border border-white/10 rounded px-3 py-1.5 text-sm text-white"
                        data-testid="dup-to" />
                    </div>
                    <Button size="sm" variant="outline" onClick={loadDuplicates}
                      data-testid="dup-scan">Scan</Button>
                    {!!(duplicates?.count && dupFrom && dupTo) && (
                      <Button size="sm" onClick={combineAll} disabled={combiningAll}
                        className="bg-amber-500 hover:bg-amber-600 text-slate-950"
                        data-testid="combine-all">
                        {combiningAll ? "Folding..." : `Combine all ${duplicates.count}`}
                      </Button>
                    )}
                  </div>
                  {!duplicates ? (
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  ) : duplicates.count === 0 ? (
                    <p className="text-sm text-slate-400" data-testid="duplicates-empty">
                      Nothing doubled up.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-400">
                        {duplicates.count} to look at
                      </p>
                      {duplicates.groups.slice(0, 40).map((g) => (
                        <div key={`${g.date}-${g.start_time}-${g.group}`}
                          className="flex flex-wrap items-center justify-between gap-3 p-3 rounded border border-white/10"
                          data-testid={`dup-${g.date}-${g.group}`}>
                          <div className="min-w-0">
                            <p className="text-sm text-slate-200">
                              {dayLabel(g.date)} · {g.start_time || "--"} · {g.group}
                            </p>
                            <p className="text-xs text-slate-500">
                              {g.sessions.length} copies ·{" "}
                              {g.sessions.map((s) =>
                                `${s.programme} (${s.signups} signed up, ${s.marked} marked)`
                              ).join(" · ")}
                            </p>
                          </div>
                          <Button size="sm" onClick={() => combine(g)}
                            disabled={combining === g.date + g.start_time}
                            data-testid={`combine-${g.date}-${g.group}`}>
                            {combining === g.date + g.start_time ? "Combining..." : "Combine"}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                <Card className="p-4 bg-slate-900/50 border-2 border-white/5"
                  data-testid="unbilled-card">
                  <h3 className="font-semibold text-white mb-1">
                    Trained before they were registered
                  </h3>
                  <p className="text-xs text-slate-400 mb-4">
                    Sessions marked present that no registration covers. Charging adds them to the
                    parent's account at their own per-session rate.
                  </p>
                  {!unbilled ? (
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  ) : unbilled.children.length === 0 ? (
                    <p className="text-sm text-slate-400" data-testid="unbilled-empty">
                      Nothing outstanding.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-400">
                        ${unbilled.total.toFixed(2)} across {unbilled.children.length} children
                      </p>
                      {unbilled.children.map((c) => (
                        <div key={c.player_id}
                          className="flex flex-wrap items-center justify-between gap-3 p-3 rounded border border-white/10"
                          data-testid={`unbilled-${c.player_id}`}>
                          <div className="min-w-0">
                            <p className="text-sm text-slate-200">
                              {c.child_name}
                              {c.membership_number && (
                                <span className="text-slate-500"> · {c.membership_number}</span>
                              )}
                            </p>
                            <p className="text-xs text-slate-500">
                              {c.sessions.length} session{c.sessions.length === 1 ? "" : "s"} ·{" "}
                              {c.sessions.map((s) => s.date).join(", ")} · ${c.rate.toFixed(2)} each
                            </p>
                            <p className="text-xs text-amber-300/80" data-testid={`unbilled-why-${c.player_id}`}>
                              {c.registered_on
                                ? `Registered ${c.registered_on} — pro-rated invoice starts then, so these are not in it`
                                : "Not registered yet — link or register them, then charge"}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-white">
                              ${c.amount.toFixed(2)}
                            </span>
                            <Button size="sm" onClick={() => chargeChild(c)}
                              disabled={charging === c.player_id || !c.parent_id}
                              title={c.parent_id ? "" : "Link this guest to a registered child first"}
                              data-testid={`charge-${c.player_id}`}>
                              {charging === c.player_id ? "Charging..." : "Charge"}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
